'use strict';

import {
    CubeCamera, EquirectangularReflectionMapping, Euler,
    Group, LinearFilter, LinearMipmapLinearFilter, LinearSRGBColorSpace,
    LoadingManager, Matrix4, MeshStandardMaterial, NoToneMapping, RepeatWrapping, Scene, Spherical,
    TextureLoader,
    Vector3,
    WebGLCubeRenderTarget,
} from 'three';
import {
    Buildings,
    components,
    GSAPAnimationMixer,
    isMobile,
    isTablet,
    settingsData,
    threeJsVariables,
} from '../../../settings';
import { generateRegion } from '../../threejs/sceneBackground';
import { Cloudflare } from '../../api/Cloudflare';
import gsap from "gsap";
import {CardTypes, ViewMode} from '../../../common';
import {
    findSelectionAndCrmData, freezingCanvas,
    isCompareMode, isFlatHas360Observation,
    isPortraitMode,
    userPrevent,
} from '../../../functions';
import { UnitCardClass } from '../../cards/unitCards/UnitCardClass';
import { BaseElement } from '../../interfaces/BaseElement';
import { renderUpdateValuesOnDemand } from '../../../app';
import { setActiveLordIcon, setLordIcon } from '../../../lordicon';
import { CubemapToEquirectangular } from '../../../libs/CubemapToEquirectangular';
import { TraverseSwitcher } from '../TraverseSwitcher/TraverseSwitcher';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from '../../../libs/OrbitControls';
import { ResizeObserver } from 'resize-observer';
import { Composer } from '../../post-processing/Composer';
import { CylinderBase } from '../../cylinderGrid/CylinderBase';
import { hideBuildings, showAllBuildings } from '../building-switch/building_switch';
import {hideSceneObjects, sceneObjectsGroup, showSceneObjects} from '../../points/points';
import { updateComponentsOnCanvasResize } from '../../menu/search/search';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { floorMoveAnimation } from '../../controls/destroyBuilding';
import { PhotographicViewer } from './PhotographicViewer';
import {getCurrentLangDirection} from "../langSwitcher/langSwitcher";
import { DreamsPreloader } from '../preloader/DreamsPreloader';
import { addCardButtonTooltip, addDisclaimer } from '../../cards/optionsCards';
import { OptionsCardViewer } from '../../cards/OptionsCardViewer';
import {IntersectionLegenda} from "../IntersectionLegenda";


let firstScene = null;
let secondScene = null;
const tempVector = new Vector3();
const spherical = new Spherical();
const pointFrom = new Vector3();
const pointTo = new Vector3();

export class Vantages {
    static #objectToIntersection;
    static textures = {};
    static scopes = [];
    static sphere = null;
    static startRendering = false;
    static type = config.features.vantage?.type || '';
    static active = config.features.vantage?.active || false;
    #buildings = config.features.vantage?.buildings || threeJsVariables.bundleConfig?.vantage || {};

    constructor (buildingEngineId) {
        this.buildingEngineId = buildingEngineId;
        this.engineId = null;
        this.floors = this.#buildings[this.buildingEngineId];
    }

    addButton(parent) {
        if ((OptionsCardViewer.active && !Vantages.isPhotographic())) return;

        const engineId = parent.closest('.options-card').getAttribute('data-engineid');
        const crm = settingsData.allCrmData.find(el => el.engineId === engineId);
        const valid = this.validate(crm);
        if (!valid) return;

        this.engineId = engineId;
        const btn = new BaseElement('button')
          .addClass('vantage', `${CardTypes.Options}-card__plans-box_buttons-btn`,'vantage-mode-btn', isMobile ? 'icon-vantage' : null)
          .addAttribute('data-type', 'vantage')
          .event('click', this.enter.bind(this,parent))
          .appendTo(parent);
        !isMobile && setLordIcon('vantage', btn.DOMElement, {
            stroke: 90,
            trigger: 'hover'
        });

        addCardButtonTooltip(btn.DOMElement,'vantage');
    }

    validate(crm) {
        return Boolean(this.floors && this.floors.find(el => crm.floorNum >= el.from && crm.floorNum <= el.to)?.url?.length) ||
            Boolean(this.floors && this.floors.find(el => crm.floorNum >= el.from && crm.floorNum <= el.to)?.folder?.length);
    }

    findClosestVantage(selectedMesh) {
        let vantage = this.floors.find(el => el.name === selectedMesh.name);

        if (!vantage.url) {
            const tempMatrix = new Matrix4();
            const tempPosition = new Vector3();
            const currentMatrix = new Matrix4().fromArray(vantage.matrix);
            const currentVector = new Vector3().fromArray(vantage.pointFrom).applyMatrix4(currentMatrix);
            const distances = this.floors.map(data => {
                if (data.url) {
                    tempMatrix.fromArray(data.matrix);
                    tempPosition.fromArray(data.pointFrom);
                    tempPosition.applyMatrix4(tempMatrix);
                    return tempPosition.distanceTo(currentVector);
                } else {
                    return Infinity;
                }
            });

            const minDistance = Math.min(...distances);
            const minIndex = distances.indexOf(minDistance);
            vantage = this.floors[minIndex];
        }

        return vantage;
    }

    async enter(parent,e) {
        const asideContainer = document.querySelector('.aside__container');
        if (asideContainer) {
            asideContainer.style.position = 'fixed';
        }
        
        if (isMobile){
            freezingCanvas(false);
            components.mobileSearch?.hide();
        }

        if (Vantages.isPhotographic()) {
            this.card = parent.closest('.options-card');
            const planBlock = this.card.querySelector('.options-card__plans-box');
            this.setButtonActive(e);
            addDisclaimer(this.card,parent.querySelector('.vantage-mode-btn'));
            this.data = findSelectionAndCrmData(this.card.getAttribute('data-engineid'));
            threeJsVariables.renderer.setScissorTest(true);
            renderUpdateValuesOnDemand(false);
            let floorVantage = this.findClosestVantage(this.data.selectedMesh);

            this.viewer = new PhotographicViewer(this.card,this.data,floorVantage);

            if (!isCompareMode()) {
                let uniqueFloors = this.floors.map(data => data.from);
                uniqueFloors = [...new Set(uniqueFloors)];
                uniqueFloors = uniqueFloors.map(number => {
                    return {
                        from: number,
                        to: number
                    }
                });

                this.switcher = new VantageSwitcher(planBlock,this.data.selectedMesh,floorVantage,uniqueFloors,this);
                this.switcher.show();
            }

            Vantages.generalElementsVisibility('hide');
            this.setElementsVisibility('hide');
            Vantages.scopes.push(this);
            Vantages.startRendering = true;

        } else {
            if (!Vantages.sphere) {
                Vantages.sphere = Vantages.createSphere();
            }
            Vantages.setBuildingsStateOn('enter');
            Vantages.#objectToIntersection = settingsData.objects_to_intersection.slice();
            if (threeJsVariables.background) {
                threeJsVariables.background.visible = false;
            }
            // threeJsVariables.background.children.forEach(child => {
            //     child.visible = false;
            // })

            // const isFloorPlanActive = settingsData.activeViewMode === ViewMode.FloorPlan
            // isFloorPlanActive && projectMode.FloorPlan.exitFromMode();
            // if (threeJsVariables.slicer.active) {
            //     const mixer = GSAPAnimationMixer.registerNewMixer('enterToVantage');
            //     const slicerAnimation = threeJsVariables.slicer.restoreXZ();
            //     mixer.addAnimation(slicerAnimation);
            //     mixer.play();
            // }

            this.manager = new LoadingManager();
            this.textureLoader = new TextureLoader(this.manager);

            threeJsVariables.renderer.setScissorTest(true);
            renderUpdateValuesOnDemand(false);
            // threeJsVariables.renderer.shadowMap.autoUpdate = true;

            this.card = parent.closest('.options-card');
            const planBlock = this.card.querySelector('.options-card__plans-box');
            addDisclaimer(this.card,parent.querySelector('.vantage-mode-btn'));
            this.setButtonActive(e);
            let preloader = null;

            this.manager.onStart = () => {
                preloader =  new BaseElement()
                    .addClass('vantage-preloader')
                    .DOMElement;
                planBlock.appendChild(preloader);
                new DreamsPreloader(preloader,'vantage-preloader').show();
            };

            this.manager.onLoad = () => {
                this.viewer.element.classList.remove('invisible');
                renderUpdateValuesOnDemand(false);
                preloader?.remove();
                // threeJsVariables.renderer.shadowMap.autoUpdate = false;
            };

            this.data = findSelectionAndCrmData(this.card.getAttribute('data-engineid'));
            const floorVantage = this.floors.find(el => this.data.crmDataOfFlat.floorNum >= el.from && this.data.crmDataOfFlat.floorNum <= el.to);
            const textures = this.getTextures(floorVantage);
            const vectors = this.calculateVectors(this.data.selectedMesh);
            const is360Observation = isFlatHas360Observation(this.data.selectedMesh);

            this.viewer = new VantageViewer(planBlock,floorVantage,this.data.selectedMesh);
            this.viewer.updateVisibility('show');
            this.viewer.calculateViewport();
            this.viewer.addSphere(Vantages.sphere,textures);
            // this.viewer.toggleSceneObjects('hide');

            // this.transferObjectsToViewer(vectors.realBalcony);
            this.viewer.setCameraPosition(vectors.orbitVector,vectors.cameraVector);
            this.viewer.applySphereAndCameraSettings();
            this.setObservationType(vectors, is360Observation);
            this.viewer.setPolarAngleLimit(vectors.orbitVector);
            // this.viewer.spherePosition = vectors.meshPosition;

            this.balcony = new Balcony(this.manager);
            this.balcony
                .load()
                .then((model) => {
                    if (model === null) return;

                    const lightGroup = threeJsVariables.scene.getObjectByName('light_group');
                    this.viewer.addToScene(lightGroup);
                    this.viewer.addToScene(this.balcony.model,false);

                    this.balcony.furnitureType = null;
                    this.balcony.position = vectors.orbitVector;
                    this.balcony.type(vectors.balconyVector,this.balcony.parseBalconyType(this.data.selectedMesh));

                    if (is360Observation)
                        this.balcony.visibility = false;
                });

            if (!isCompareMode()) {
                this.switcher = new VantageSwitcher(planBlock,this.data.selectedMesh,floorVantage,this.floors,this);
                this.switcher.show();
            }

            Vantages.generalElementsVisibility('hide');
            this.setElementsVisibility('hide');
            Vantages.scopes.push(this);
            Vantages.startRendering = true;
        }
    }

    setObservationType(vectors, is360Observation) {
        if (is360Observation) {
            this.viewer.set360Rotation();
        } else {
            this.viewer.setRotationLimits(vectors.balconyVector);
        }
    }

    setButtonActive(e) {
        let target;

        e.target.tagName === 'LORD-ICON' ?
          target = e.target.parentElement:
          target = e.target;
        const btnList = this.card.querySelectorAll(`.${CardTypes.Options}-card__plans-box_buttons-btn`);
        btnList.forEach(btn => {
            if (btn.classList.contains('active')) {
                btn.classList.remove('active');
                if (!isMobile) {
                    btn.removeAttribute('disabled');
                    setActiveLordIcon(undefined, this.card.querySelector(`.${CardTypes.Options}-card__plans-box_buttons`));
                }
            }
        });
        if (!isMobile) {
            target.setAttribute('disabled', 'disabled');
            setActiveLordIcon(target.getAttribute('lord-icon-name'), this.card.querySelector(`.${CardTypes.Options}-card__plans-box_buttons`));
        }
        target.classList.add('active');
    }

    changeFloor(mesh) {
        if (Vantages.isPhotographic()) {
           const floorVantage = this.findClosestVantage(mesh);
           this.viewer.update(floorVantage);
        } else {
            const is360Observation = isFlatHas360Observation(mesh);
            const floorVantage = this.floors.find(el => mesh.userData.crm_data.floorNum >= el.from && mesh.userData.crm_data.floorNum <= el.to);
            if (!floorVantage) return;
            const textures = this.getTextures(floorVantage);
            const vectors = this.calculateVectors(mesh);
            this.viewer.setData(floorVantage);
            this.viewer.setApartment(mesh);
            this.viewer.updateSphereTextures(textures);
            this.viewer.setPolarAngleLimit(vectors.orbitVector);
            this.setObservationType(vectors, is360Observation);
            this.viewer.setCameraPosition(vectors.orbitVector,vectors.cameraVector);
            this.viewer.applySphereAndCameraSettings();
            // this.viewer.spherePosition = vectors.meshPosition;
            this.balcony.furnitureType = null;
            this.balcony.position = vectors.orbitVector;
            this.balcony.type(vectors.balconyVector,this.balcony.parseBalconyType(mesh));
            is360Observation
                ? this.balcony.visibility = false
                : this.balcony.visibility = true;
        }
    }

    getTextures(floorData) {
        let textures = Vantages.textures[this.buildingEngineId]?.[floorData.folder];

        if (!textures) {
            this.loadTextures(floorData.folder);
            textures = Vantages.textures[this.buildingEngineId][floorData.folder];
        }

        return textures;
    }

    transferObjectsToViewer(useRealBalcony) {
        // return;
        // const buildings = threeJsVariables.scene.getObjectByName('Project Buildings').clone(true);
        // if (!useRealBalcony) {
        //     buildings.children = buildings.children.filter(building => building.name !== this.buildingEngineId);
        // }
        // if (buildings.children.length) {
        //     this.viewer.addToScene(buildings,true,true,useRealBalcony);
        // }
        // const lightGroup = threeJsVariables.scene.getObjectByName('light_group');
        // this.viewer.addToScene(lightGroup,true,true);
        //
        // if (this.#objects.length) {
        //     let sceneObjects = threeJsVariables.scene.getObjectByName('Scene objects');
        //     if (!sceneObjects)
        //         return;
        //
        //     sceneObjects = sceneObjects.clone(true);
        //     const objectsToVisibility = [];
        //     this.#objects.forEach(obj => {
        //         const object = sceneObjects.children.find(el => el.name === obj);
        //         if (object) {
        //             objectsToVisibility.push(object);
        //         }
        //     });
        //
        //     if (objectsToVisibility.length) {
        //         sceneObjects.children = objectsToVisibility;
        //         this.viewer.addToScene(sceneObjects);
        //     }
        // }
    }

    calculateVectors(mesh) {
        if (mesh.children.length) {
            const balconyPointFrom = mesh.children.find(el => el.name.startsWith('balconyFrom'));
            const balconyPointTo = mesh.children.find(el => el.name.startsWith('balconyTo'));
            if (balconyPointFrom && balconyPointTo) {
                balconyPointFrom.getWorldPosition(pointFrom);
                balconyPointTo.getWorldPosition(pointTo);
            } else {
                mesh.getWorldPosition(pointTo);
                mesh.parent.getWorldPosition(pointFrom);
            }
        } else {
            mesh.getWorldPosition(pointTo);
            mesh.parent.getWorldPosition(pointFrom);
        }


        const direction = new Vector3().subVectors(pointTo,pointFrom).normalize().setY(0);
        direction.multiplyScalar(0.1);
        const target = new Vector3().copy(pointFrom).add(direction);
        const directionNormalize = new Vector3().subVectors(pointTo, pointFrom).normalize();

        return {
            orbitVector: target,
            cameraVector: pointFrom,
            balconyVector: directionNormalize
        }
    }

    loadTextures(folder) {
        let vantageQualityParams = null;
    
        if (config.features.vantage.cloudflare) {
            if (isMobile || isTablet) {
                vantageQualityParams = config.features.vantage.cloudflare.mobile || null;
            } else {
                vantageQualityParams = config.features.vantage.cloudflare.web || null;
            }
        }
        
        Vantages.sphere.traverse(el => {
            if (el.isMesh) {
                const url = Cloudflare.resize(
                  `${config.cdnRepository}/${config.projectFolder}/panocontent/vantages3D/${this.buildingEngineId}/${folder}/${el.name}.jpg`,vantageQualityParams
                );

                const texture = this.textureLoader.load(url);
                this.saveTexture(folder,el.name,texture);
            }
        });
    }

    saveTexture(folder,name,texture) {
        if (!Vantages.textures[this.buildingEngineId]) {
            Vantages.textures[this.buildingEngineId] = {};
        }

        if (!Vantages.textures[this.buildingEngineId][folder]) {
            Vantages.textures[this.buildingEngineId][folder] = {};
        }

        Vantages.textures[this.buildingEngineId][folder][name] = texture;
    }

    setElementsVisibility(action) {
        const printBtn = this.card.querySelector('.options-card__plans-box_buttons-btn.print');
        const paint = this.card.querySelector('.options-card__plans-box_buttons-btn.paint-2d');
        const contactWidget = document.querySelector('.contact-widget__main-container');

        switch (action) {
            case 'hide':
                this.card.classList.add('vantage-mode');
                UnitCardClass.cards.forEach(card => {
                   card.html.classList.add('hidden');
                });
                printBtn?.classList.add('hidden');
                paint?.classList.add('hidden');
                contactWidget?.classList.add('hidden');
                break;
            case 'show':
                this.card.classList.remove('vantage-mode');
                UnitCardClass.cards.forEach(card => {
                    if (!isCompareMode())
                        card.html.classList.remove('hidden');
                });
                printBtn?.classList.remove('hidden');
                paint?.classList.remove('hidden');
                contactWidget?.classList.remove('hidden');
                break;
        }
    }

    async close() {
        this.setElementsVisibility('show');
        await this.viewer.dispose();
        this.switcher?.dispose();
        this.balcony = null;
        this.switcher = null;
        this.viewer = null;
        Vantages.scopes = Vantages.scopes.filter(scope => scope !== this);
    }

    static isPhotographic() {
        return Vantages.type === 'photographic';
    }

    static generalElementsVisibility(state) {
        const cylinderFloorNumbers = threeJsVariables.scene.getObjectByName('cylinderFloorNumbers');
        const compass = document.querySelector('.compass-container');
        const buildingSwitcher = document.querySelector('#building-switcher');
        const rightNavBar = document.querySelector('.right-nav-bar');
        const cssLayer = document.querySelector('#three_d_css');
        const search = document.querySelector('.search__container-main');
        // const intersectionLegenda = document.querySelector('.intersection-legenda');
        const compareBlock = document.querySelector('.compare-block');
        const compareBtn = document.querySelector('.compare-button');
        const flatTooltip = document.querySelector('.flat-tooltip');
        const amenitiesContainer = document.querySelector('.amenities__main__container');
        const googleMiniMap = document.querySelector('.google-mini-map');

        switch (state) {
            case 'hide':
                cssLayer?.classList.add('hidden');
                search?.classList.add('hidden');
                search?.classList.add('vantage-mode');
                compass.classList.add('hidden');
                flatTooltip?.classList.add('hidden');
                buildingSwitcher?.classList.add('hidden');
                rightNavBar?.classList.add('transparent');
                // intersectionLegenda?.classList.add('hidden');
                IntersectionLegenda.hide();
                compareBlock?.classList.add('vantage-mode');
                amenitiesContainer?.classList.add('hidden');
                googleMiniMap?.classList.add('hidden');
                if (cylinderFloorNumbers) {
                    cylinderFloorNumbers.visible = false;
                }
                if (isCompareMode()) {
                    UnitCardClass.cards.forEach(card => {
                        card.html.classList.add('hidden');
                    });
                }
                compareBtn?.classList.add('hidden');

                break;
            case 'show':
                cssLayer?.classList.remove('hidden');
                if (cylinderFloorNumbers) {
                    cylinderFloorNumbers.visible = true;
                }
                if (isCompareMode()) {
                    UnitCardClass.cards.forEach(card => {
                        card.html.classList.remove('hidden');
                    });
                }
                compareBtn?.classList.remove('hidden');
                search?.classList.remove('hidden');
                flatTooltip?.classList.remove('hidden');
                compass.classList.remove('hidden');
                buildingSwitcher?.classList.remove('hidden');
                rightNavBar?.classList.remove('transparent');
                // intersectionLegenda?.classList.remove('hidden');
                IntersectionLegenda.show();
                amenitiesContainer?.classList.remove('hidden');
                compareBlock?.classList.remove('vantage-mode');
                googleMiniMap?.classList.remove('hidden');
                setTimeout(() => search?.classList.remove('vantage-mode'),500);
                break;
        }
    }

    static createSphere() {
        const group = new Group();
        group.name = 'Vantage';
        const m1 = generateRegion([0,0,-1],[0,1,0],[ 1,0,0]);
        const m2 = generateRegion([0,0, 1],[0,1,0],[-1,0,0]);
        const m3 = generateRegion([ 1,0,0],[0,1,0],[0,0, 1]);
        const m4 = generateRegion([-1,0,0],[0,1,0],[0,0,-1]);
        const m5 = generateRegion([0,0, 1],[1,0,0],[0, 1,0]);
        const m6 = generateRegion([0,0,-1],[1,0,0],[0,-1,0]);
        m1.name = 'nz';
        m2.name = 'pz';
        m3.name = 'px';
        m4.name = 'nx';
        m5.name = 'py';
        m6.name = 'ny';
        m6.rotation.y = Math.PI;
        group.add(m1);
        group.add(m2);
        group.add(m3);
        group.add(m4);
        group.add(m5);
        group.add(m6);
        m1.material.transparent = false;
        m2.material.transparent = false;
        m3.material.transparent = false;
        m4.material.transparent = false;
        m5.material.transparent = false;
        m6.material.transparent = false;
        m1.material.depthWrite = true;
        m2.material.depthWrite = true;
        m3.material.depthWrite = true;
        m4.material.depthWrite = true;
        m5.material.depthWrite = true;
        m6.material.depthWrite = true;
        const rotationVector = new Vector3();
        const scaleVector = new Vector3();

        if (threeJsVariables.bundleConfig) {
            const bundleSphere = threeJsVariables.bundleConfig.sphere;

            if (config.features.vantage?.rotation) {
                rotationVector.set(0,config.features.vantage.rotation,0);
            } else {
                rotationVector.set(bundleSphere.rotation[0],bundleSphere.rotation[1],bundleSphere.rotation[2]);
            }

            scaleVector.set(bundleSphere.scale[0],bundleSphere.scale[1],bundleSphere.scale[2]);
        } else {
            const configSphere = config.resources.background;
            rotationVector.set(configSphere.rotation.x,configSphere.rotation.y,configSphere.rotation.z);
            scaleVector.set(configSphere.scale.x,configSphere.scale.y,configSphere.scale.z);
        }

        group.rotation.set(rotationVector.x,rotationVector.y,rotationVector.z);
        group.scale.copy(scaleVector);
        group.name = 'Vantage';

        return group;
    }

    static isActive() {
        return Vantages.startRendering;
    }

    static async close(callback = () => {},engineId) {
        if (engineId) {
            const scope = Vantages.scopes.find(scope => scope.engineId === engineId);
            scope && scope.close();
            if (!Vantages.isPhotographic()) {
                settingsData.objects_to_intersection = Vantages.#objectToIntersection.slice();
                if (config.resources.background.cylinderBg === 'sky')
                    threeJsVariables.background.visible = true;
            }
        } else {

            // const isFloorPlanActive = settingsData.activeViewMode === ViewMode.FloorPlan
            // isFloorPlanActive && projectMode.FloorPlan.enter();
            // if (threeJsVariables.slicer.active) {
            //     const mixer = GSAPAnimationMixer.registerNewMixer('exitFromVantage');
            //     const slicerAnimation = threeJsVariables.slicer.cropXZ(0);
            //     mixer.addAnimation(slicerAnimation);
            //     mixer.play();
            // }

            if (!Vantages.isPhotographic()) {
                Vantages.setBuildingsStateOn('close');
                settingsData.objects_to_intersection = Vantages.#objectToIntersection.slice();
                if (config.resources.background.cylinderBg === 'sky')
                    threeJsVariables.background.visible = true;
            }

            for (const scope of Vantages.scopes) {
               await scope.close();
            }
        }

        if (!Vantages.scopes.length) {
            Vantages.startRendering = false;
            threeJsVariables.renderer.setScissorTest(false);
            Vantages.generalElementsVisibility('show');
        }

        callback();
    }

    static setBuildingsStateOn(action) {
        const prevDuration = GSAPAnimationMixer.duration;
        GSAPAnimationMixer.duration = 0.1;

        const isFloorPlanActive = settingsData.activeViewMode === ViewMode.FloorPlan;
        const direction = action === 'close' ? 'destroy' : 'appear';
        const state = action === 'close' ? 'enter' : 'exit';

        if (isFloorPlanActive) {
            const mixer = GSAPAnimationMixer.registerNewMixer('floorPlan');
            floorMoveAnimation(settingsData.currentFloor, direction,undefined,state);
            mixer.play();
        }

        GSAPAnimationMixer.duration = prevDuration;
    }

    static render(delta) {
        if (Vantages.isActive()) {
            Vantages.scopes.forEach(scope => scope.viewer.draw(delta));
        }
    }

    static getEquirectangular(size) {
        Vantages.scopes[0]?.viewer.getEquirectangular(size);
    }
}

class VantageSwitcher extends TraverseSwitcher {
    constructor (parent,currentMesh,currentFloor,floors,vantageScope) {
        super(parent);

        this.vantageFloors = floors.slice().sort((a, b) => a.from - b.from);
        this.currentFloor = currentFloor;
        this.mesh = currentMesh;
        this.element.style.opacity = '';
        this.realVantage = true;
        this.vantageScope = vantageScope;

        this.addFloorButtons();
        this.setCurrentFloorButtonInFocus();
        this.checkControlButtonsVisibility();
    }

    addFloorButtons() {
        if (!this.realVantage)
            return;

        this.vantageFloors.forEach((floors,index) => {
            if (floors.from === floors.to) {
                new BaseElement()
                  .addClass('traverse-switcher__position')
                  .innerHtml(`${floors.from}`)
                  .addAttribute('data-floors',`${floors.from}-${floors.to}`)
                  .addAttribute('data-index',index)
                  .appendTo(this.floorButtons);
            } else {
                new BaseElement()
                  .addClass('traverse-switcher__position')
                  .innerHtml(`${floors.from}-${floors.to}`)
                  .addAttribute('data-floors',`${floors.from}-${floors.to}`)
                  .addAttribute('data-index',index)
                  .appendTo(this.floorButtons);
            }
        });

        this.addControlButtons();
    }

    setCurrentFloorButtonInFocus() {
        if (!this.realVantage) return;

        this.getElementsStyleParams();

        let index = null;
        if (this.currentFloor) {
            const currentFloorElement = this.floorButtons.querySelector(`div[data-floors="${this.currentFloor.from}-${this.currentFloor.to}"]`);
            index = [...this.floorButtons.childNodes].indexOf(currentFloorElement);
        } else {
            for (let i = 0; i < this.floorButtons.children.length; i ++) {
                const [from,to] = this.floorButtons.children[i].getAttribute('data-floors').split('-');
                if (this.mesh.userData.floor >= Number(from) && this.mesh.userData.floor <= Number(to)){
                    index = i;
                }
            }
        }

        gsap.to(this.floorButtons, {top: (0 - this.buttonsWindowHeight) * index});
        this.currentActiveButtonIndex = index;
    }

    controlButtonsHandler(event) {
        if (userPrevent())
            return;

        this.getElementsStyleParams();

        if (event.target.classList.contains('traverse-switcher__control-button-up')) {
            userPrevent(true);
            this.moveItemsUp();
        } else if (event.target.classList.contains('traverse-switcher__control-button-down')) {
            userPrevent(true);
            this.moveItemsDown();
        }

        const activeFloor = this.floorButtons.childNodes[this.currentActiveButtonIndex].getAttribute('data-floors');
        const [from, to] = activeFloor.split('-');
        const activeBuilding = Buildings.getActiveBuilding();
        const {floorSelectionGroup} = activeBuilding.getFloors(Number(from) - 1,Number(to));

        const rangeApartments = floorSelectionGroup
          .reduce((acc,floor) => {
              const floorApartments = floor.children.filter(el => el.userData.crm_data);
              if (floorApartments.length) {
                  acc.push(...floorApartments);
              }
              return acc;
          },[]);

        const currentMeshVector = this.mesh.getWorldPosition(new Vector3());
        const distances = {};
        rangeApartments.forEach(apt => {
            apt.getWorldPosition(tempVector);
            tempVector.y = currentMeshVector.y;
            const distance = tempVector.distanceTo(currentMeshVector);
            distances[distance] = apt;
        });

        const minimalDistance = Math.min(...Object.keys(distances));
        const mesh = distances[minimalDistance];
        this.vantageScope.changeFloor(mesh);
        this.checkControlButtonsVisibility();
    }

    dispose() {
        this.element?.remove();
    }
}

class Balcony {
    static params = config.features.vantage?.balcony || {active: false};
    static url = Balcony.params.source === 'storage' ?
      `${config.cdnRepository}/DreamsGeneralStorage/balcony/`:
      `${config.cdnRepository}/${config.projectFolder}/panocontent/vantages3D/balcony/`;
    static model = null;

    constructor (manager) {
        if (!this.active) return;

        this.manager = manager;
    }

    async load() {
        if (!this.active) return new Promise(resolve => resolve(null));
        if (Balcony.model === null) {
            return new Promise(async resolve => {
                const group = await Balcony.load(this.manager);
                const balconyGroup = new Group();
                balconyGroup.name = 'Balcony';
                balconyGroup.add(...group);
                Balcony.model = balconyGroup;
                this.balconyModel = Balcony.model.clone(true);
                resolve(this.balconyModel);
            });
        } else {
            return new Promise(resolve => {
                this.balconyModel = Balcony.model.clone(true);
                resolve(this.balconyModel);
            });
        }
    }

    type(directionVec,type) {
        if (!this.active) return;
        const theta = spherical.setFromVector3(directionVec).theta;
        const balconyPositionCorrection = tempVector.copy(directionVec).negate();

        switch (type) {
            case 0:
                this.rotation = theta;
                // balconyPositionCorrection.setLength(1.5);
                break;
            case 1:
                this.rotation = theta + Math.PI / 4;
                // balconyPositionCorrection.setLength(1.5);
                break;
            case 2:
                this.rotation = theta + Math.PI / 2;
                // balconyPositionCorrection.setLength(2.2);
                break;
            case 3:
                this.rotation = theta - Math.PI / 4 + Math.PI;
                // balconyPositionCorrection.setLength(2);
                break;
            case 4:
                this.rotation = theta - Math.PI;
                // balconyPositionCorrection.setLength(1.5);
                break;
            case 5:
                this.rotation = theta - Math.PI + Math.PI / 4;
                // balconyPositionCorrection.setLength(2);
                break;
            case 6:
                this.rotation = theta - Math.PI / 2;
                // balconyPositionCorrection.setLength(2.2);
                break;
            case 7:
                this.rotation = theta - Math.PI / 4;
                // balconyPositionCorrection.setLength(1.5);
                break;
            default:
                this.rotation = theta;
                // balconyPositionCorrection.setX(0);
                // balconyPositionCorrection.setZ(0);
                break;
        }

        balconyPositionCorrection.setY(-1);
        this.balconyModel.position.add(balconyPositionCorrection);
    }

    remove() {
        if (!this.active || (this.balconyModel && !this.balconyModel.parent)) return;

        this.balconyModel.removeFromParent();
        this.balconyModel.traverse(el => {
           if (el.isMesh) {
               el.geometry.dispose();
               el.material.dispose();
           }
        });
    }

    parseBalconyType(mesh) {
        if (!this.active) return Balcony.params.defaultType;

        const text = mesh.name.match(/balcony\d/);
        if (text) {
           return Number(text[0].replace('balcony',''));
        } else {
            return Balcony.params.defaultType;
        }
    }

    set visibility(state) {
        if (!this.active) return;
        this.balconyModel.visible = state;
    }

    set position(vector) {
        if (!this.active) return;
        this.balconyModel.position.copy(vector);
    }

    set rotation(angel) {
        if (!this.active) return;
        this.balconyModel.rotation.y = angel;
    }

    set furnitureType(number) {
        if (!this.active) return;
        if (!number) {
            number = Math.round(Math.random() * (Balcony.params.objects.length - 1));
        }

        this.balconyModel.children.forEach(el => {
            if (el.userData.type !== 'balcony') {
                el.visible = el.userData.type === `object-${number}`;
            }
        });
    }

    get active() {
        return Balcony.params.active;
    }

    get model() {
        return this.balconyModel;
    }

    static async load(manager) {
        const promises = [];
        const textureLoader = new TextureLoader(manager);
        const fbxLoader = new FBXLoader(manager);

        //Env texture
        const buildingMaterial = Buildings.getActiveBuilding().instances[0].material;

        //Load balcony
        const balconyMaterial = Balcony.#createMaterial(Balcony.params.material,textureLoader);
        balconyMaterial.envMap = buildingMaterial.envMap;
        const balconyModel = Balcony.#loadModel(Balcony.url + Balcony.params.path,'balcony',balconyMaterial,fbxLoader);
        promises.push(balconyModel);

        //Load objects
        Balcony.params.objects.forEach((obj,index) => {
            const objectMaterial = Balcony.#createMaterial(obj.material,textureLoader);
            objectMaterial.envMap = buildingMaterial.envMap;
            const objectModel = Balcony.#loadModel(Balcony.url + obj.path,`object-${index}`,objectMaterial,fbxLoader);
            promises.push(objectModel);
        });

        return Promise.all(promises);
    }

    static #createMaterial(settings,textureLoader) {
        const material = new MeshStandardMaterial();
        Object.entries(settings).forEach(([key,value]) => {
           if (typeof value === 'string') {

               const modifiedUrl = Cloudflare.resize(Balcony.url + value, {
                   width: 1024,
                   quality: 75
               });
               const texture = textureLoader.load(modifiedUrl);
               if (key === 'envMap') {
                   texture.mapping = EquirectangularReflectionMapping;
               }
               texture.colorSpace = LinearSRGBColorSpace;
               texture.wrapS = RepeatWrapping;
               texture.wrapT = RepeatWrapping;
               texture.offset.set(0,0);
               texture.repeat.set(1,1);
               texture.anisotropy = 2;
               texture.magFilter = LinearFilter;
               texture.minFilter = LinearMipmapLinearFilter;

               material[key] = texture;
           } else {
               material[key] = value;
           }
        });

        return material;
    }

    static #loadModel(path,type,material,fbxLoader) {
        return new Promise((resolve, reject) => {
            fbxLoader.load(path, model => {
                model.traverse(el => {
                    if (el.isMesh) {
                        el.material = material;
                        el.castShadow = true;
                        el.receiveShadow = true;
                    }
                });
                model.userData.type = type;
                resolve(model);
            },undefined,() => reject());
        });
    }
}

class VantageViewer {

    constructor (parent,data,apartment) {
        if (firstScene === null && secondScene === null) {
            firstScene = new Scene();
            secondScene = new Scene();
        }

        this.data = data;
        this.apartment = apartment;
        this.sphereScene = firstScene.children.length ? secondScene : firstScene;
        this.scene = threeJsVariables.scene;
        this.camera = threeJsVariables.perspectiveCamera.clone();
        this.renderer = threeJsVariables.renderer;
        this.canvas = this.renderer.domElement;
        this.composer = new Composer(this.renderer,this.scene,this.camera);
        this.objectsToHide = config.features.vantage?.objectsToHide || [];

        //Apply effect settings
        Object.entries(threeJsVariables.bundleConfig.effects).forEach(([name,settings]) => {
            const effect = this.composer.getPass(name);
            if (effect) {
                effect.config = settings;
            }
        });

        this.card = parent.closest('.options-card');
        this.element = new BaseElement()
            .addClass('vantage-viewer', isMobile ? 'invisible' : null)
            .appendTo(parent)
            .DOMElement;

        this.sphere = null;
        this.controls = new OrbitControls(this.camera,this.element);
        this.controls.addEventListener('end', () => renderUpdateValuesOnDemand(false));
        this.viewport = {};
        this.observer = new ResizeObserver(() => {
            this.calculateViewport();
            setTimeout(() => this.calculateViewport(), 100);
        });

        this.observer.observe(this.element);
        this.controls.maxPolarAngle = 1.41371669411541;
        this.controls.rotateSpeed = 1;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.reverseControls = true;
        this.controls.rotateSpeed = 0.15;
        this.camera.near = 0.1;


    }

    setApartment(mesh) {
        this.apartment = mesh;
    }

    setData(data) {
        this.data = data;
    }

    applySphereAndCameraSettings() {
        let settings = null;

        if (this.data.exceptions && this.data.exceptions.length) {
            const findSettings = this.data.exceptions.find(obj => obj.name === this.apartment.name);
            if (findSettings) {
                settings = convertDataFromJSON(findSettings);
            }
        }

        if (!settings && this.data.settings) {
            settings = convertDataFromJSON(this.data.settings);
        }

        // Apply default settings from config
        if (!settings) {
            settings = this.getOldDefaultSphereSettings();
            settings.camera = config.features.vantage.fov ? config.features.vantage.fov : threeJsVariables.perspectiveCamera.fov;
        }

        Object.entries(settings).forEach(([key,value]) => {
            if (key === 'camera') {
                this.camera.fov = value;
                this.camera.updateProjectionMatrix();
            } else {
                this.sphere[key].copy(value);
            }
        });
    }

    getOldDefaultSphereSettings() {
        const settings = {
            position: this.apartment.getWorldPosition(new Vector3()),
            rotation: new Euler(),
            scale: new Vector3()
        };

        if (threeJsVariables.bundleConfig) {
            const bundleSphere = threeJsVariables.bundleConfig.sphere;

            if (config.features.vantage?.rotation) {
                settings.rotation.set(0,config.features.vantage.rotation,0);
            } else {
                settings.rotation.set(bundleSphere.rotation[0],bundleSphere.rotation[1],bundleSphere.rotation[2]);
            }

            settings.scale.set(bundleSphere.scale[0],bundleSphere.scale[1],bundleSphere.scale[2]);
        } else {
            const configSphere = config.resources.background;
            settings.rotation.set(configSphere.rotation.x,configSphere.rotation.y,configSphere.rotation.z);
            settings.scale.set(configSphere.scale.x,configSphere.scale.y,configSphere.scale.z);
        }

        return settings;
    }

    async updateVisibility(state) {
        if (Vantages.scopes.length === 2)
            return;

        return new Promise(resolve => {
            const prevDuration = GSAPAnimationMixer.duration;
            GSAPAnimationMixer.duration = 0.1;
            const activeBuilding = Buildings.getActiveBuilding();
            if (state === 'show') {
                if (!Vantages.scopes.length) {
                    const mixer = GSAPAnimationMixer.registerNewMixer('exitToOrbit');
                    mixer.addCallback('onComplete', () => {
                        this.toggleSceneObjects('hide');
                        resolve(true);
                    });

                    const cylinderPass = threeJsVariables.composer.getPass('cylinderPass');
                    const fadeProgress = {
                        value: 1
                    };

                    const fadeAnimation = gsap.to(fadeProgress, {
                        value: 0,
                        duration: GSAPAnimationMixer.duration,
                        ease: GSAPAnimationMixer.ease,
                        onStart: () => {
                            cylinderPass.active = true;
                            showAllBuildings();
                            showSceneObjects();
                            CylinderBase.hide();
                        },
                        onUpdate: () => cylinderPass.setProgress(fadeProgress.value),
                        onComplete: () => cylinderPass.active = false
                    });

                    mixer.addAnimation(fadeAnimation);

                    // CylinderBase.hide();
                    // showAllBuildings();
                    // showSceneObjects();
                    mixer.play();
                    activeBuilding.selectionsGroup.visible = false;
                    if (config.features.vantage.balcony.active) {
                        const objectsToShow = config.features.vantage.balcony?.objectsToShow;
                        if (objectsToShow && objectsToShow.length > 0) {
                            activeBuilding.modelGroup.traverse((modelItem) => {
                                if (modelItem.type !== 'Group')
                                    modelItem.visible = false;
                                if (objectsToShow.includes(modelItem.name))
                                    modelItem.visible = true;
                            })
                        } else {
                            activeBuilding.modelGroup.visible = false;
                        }
                    }
                }
            } else {
                activeBuilding.selectionsGroup.visible = true;
                if (config.features.vantage.balcony.active) {
                    const objectsToShow = config.features.vantage.balcony?.objectsToShow;
                    if (objectsToShow && objectsToShow.length > 0) {
                        activeBuilding.modelGroup.traverse((modelItem) => {
                            modelItem.visible = true;
                        });
                    } else {
                        activeBuilding.modelGroup.visible = true;
                    }
                }
                this.sphere.removeFromParent();
                this.sphere.traverse(el => {
                    if (el.isMesh) {
                        el.geometry.dispose();
                        el.material.dispose();
                    }
                });
                const mixer = GSAPAnimationMixer.registerNewMixer('cylinderEnter');
                mixer.addCallback('onComplete', () => {
                    // this.toggleSceneObjects('show');
                    resolve(true);
                });
                // CylinderBase.show(activeBuilding);
                // this.toggleSceneObjects('show');
                // hideSceneObjects(activeBuilding);
                // hideBuildings();

                const cylinderPass = threeJsVariables.composer.getPass('cylinderPass');
                const fadeProgress = {
                    value: 0
                };

                const fadeAnimation = gsap.to(fadeProgress, {
                    value: 1,
                    duration: GSAPAnimationMixer.duration,
                    ease: GSAPAnimationMixer.ease,
                    onStart: () => {
                        this.toggleSceneObjects('show');
                        cylinderPass.active = true
                    },
                    onUpdate: () => cylinderPass.setProgress(fadeProgress.value),
                    onComplete: () => {
                        hideSceneObjects(activeBuilding);
                        hideBuildings();
                        cylinderPass.active = false;
                    }
                });

                mixer.addAnimation(fadeAnimation);
                CylinderBase.show(activeBuilding);
                // hideSceneObjects(activeBuilding);
                // hideBuildings();
                mixer.play();
            }
            GSAPAnimationMixer.duration = prevDuration;
        });
    }

    toggleSceneObjects(state) {
        if (Vantages.scopes.length === 2)
            return;

        switch(state) {
            case 'show':
                sceneObjectsGroup.traverse(sceneObject => {
                    if (this.objectsToHide.includes(sceneObject.name)) {
                        sceneObject.visible = true;
                    }
                });
                break;
            case 'hide':
                sceneObjectsGroup.traverse(sceneObject => {
                    if (this.objectsToHide.includes(sceneObject.name)) {
                        sceneObject.visible = false;
                    }
                });
                break;
        }
    }

    addToScene(obj,needClone = true) {
        if (needClone) {
            const clone = obj.clone(true);
            this.sphereScene.add(clone);
        } else {
            this.sphereScene.add(obj);
        }
    }

    addSphere(sphere,textures) {
       this.sphere = sphere.clone(true);
       this.sphere.traverse(el => {
            if (el.isMesh) {
                el.material = el.material.clone();
                el.material.uniforms.map1.value = textures[el.name];
            }
       });

        this.sphereScene.add(this.sphere);
        if (config.features.vantage.balcony.active) {
            const scenePass = new RenderPass(this.sphereScene,this.camera);
            scenePass.clear = false;
            this.composer.composer.insertPass(scenePass,1);
        } else {
            this.composer.composer.passes[0].clear = false;
            const scenePass = new RenderPass(this.sphereScene,this.camera);
            this.composer.composer.insertPass(scenePass,0);
        }

    }

    updateSphereTextures(textures) {
        this.sphere.traverse(el => {
            if (el.isMesh) {
                el.material.uniforms.map1.value = textures[el.name];
            }
        });
    }

    setCameraPosition(orbitControlsVector,cameraVector) {
        this.controls.target.copy(orbitControlsVector);
        this.camera.position.copy(cameraVector);

        // if (this.buildings) {
        //     if (useRealBalcony instanceof Vector3) {
        //         this.buildings.visible = true;
        //         const direction = new Vector3()
        //             .copy(this.controls.target)
        //             .clamp(new Vector3(-1,-1,-1),new Vector3(1,1,1))
        //             .multiply(useRealBalcony);
        //         this.controls.target.add(direction);
        //         this.camera.position.add(direction);
        //     } else {
        //         this.buildings.visible = false;
        //     }
        //
        //     // const activeBuildingName = Buildings.getActiveBuilding().name;
        //     // this.activeBuilding = this.buildings.getObjectByName(activeBuildingName);
        //     // this.reorder();
        // }

        this.controls.update();
    }

    reorder() {
        const currentOrder = ['Vantage', 'Scene objects', 'Project Buildings'];
        const parent = this.activeBuilding.parent;
        const activeBuildingIndex = parent.children.indexOf(this.activeBuilding);
        parent.children[activeBuildingIndex] = parent.children[parent.children.length - 1];
        parent.children[parent.children.length -1] = this.activeBuilding;

        currentOrder.forEach((name,index) => {
           const onScene = this.scene.getObjectByName(name);
           if (onScene) {
               const currentPosition = this.scene.children.indexOf(onScene);
               if (currentPosition !== -1 && currentPosition !== index){
                   const target = this.scene.children[index];
                   if (target) {
                       this.scene.children[index] = onScene;
                       this.scene.children[currentPosition] = target;
                   }
               }
           }
        });
    }

    setRotationLimits(vec) {
        const theta = spherical.setFromVector3(vec).theta;
        const limit = Balcony.params.active ? Math.PI / 8 : Math.PI / 4;
        this.controls.minAzimuthAngle = theta - Math.PI - limit;
        this.controls.maxAzimuthAngle = theta - Math.PI + limit;
    }

    set360Rotation() {
        this.controls.minAzimuthAngle = -Infinity;
        this.controls.maxAzimuthAngle = Infinity;
    }

    setPolarAngleLimit(vec) {
        this.controls.minPolarAngle = this.controls.maxPolarAngle - vec.y / 100;
    }

    calculateViewport() {
        const rect = this.element.getBoundingClientRect();
        if (isMobile) {
            if (isPortraitMode()) {
                this.viewport.width = rect.right - rect.left;
                this.viewport.height = rect.bottom - rect.top;
                this.viewport.left = 0;
                this.viewport.bottom = this.renderer.domElement.clientHeight - rect.height;
            } else {
                this.viewport.width = rect.right - rect.left;
                this.viewport.height = rect.bottom - rect.top;
                this.viewport.left = 0;
                this.viewport.bottom = 0;
            }
        } else {
            // const offset = getCurrentLangDirection() === 'ltr' ? 56 : 0;
            const offset = 0;
            this.viewport.width = rect.right - rect.left;
            this.viewport.height = rect.bottom - rect.top;
            this.viewport.left = rect.left - offset;
            this.viewport.bottom = this.renderer.domElement.clientHeight - rect.bottom;
        }

        this.resize();
        this.draw();
    }

    resize() {
        this.camera.aspect = this.viewport.width / this.viewport.height;
        this.camera.updateProjectionMatrix();
        this.composer.setSize(this.viewport.width,this.viewport.height);
    }

    isVisible() {
        const rect = this.element.getBoundingClientRect();
        return !(rect.bottom < 0 || rect.top > this.renderer.domElement.clientHeight ||
          rect.right < 0 || rect.left > this.renderer.domElement.clientWidth);
    }

    draw(delta) {
        this.canvas.style.transform = `translateY(${-this.card.scrollTop}px)`;
        this.canvas.style.transition = 'transform .1s';
        this.renderer.setViewport( this.viewport.left, this.viewport.bottom, this.viewport.width, this.viewport.height );
        this.renderer.setScissor( this.viewport.left, this.viewport.bottom, this.viewport.width, this.viewport.height );
        this.composer.render(delta);
    }

    async dispose() {
        await this.updateVisibility('hide');
        if (settingsData.activeViewMode === ViewMode.FloorPlan && config.resources.sceneObjects.hideAttachedModelInKeyPlan) {
            hideSceneObjects();
        }
        // this.toggleSceneObjects('show');
        while (this.sphereScene.children.length) {
            this.sphereScene.children[0].removeFromParent();
        }
        this.sphereScene = null;
        this.composer.composer.dispose();
        this.canvas.style.transform = '';
        this.canvas.style.transition = '';
        this.controls.dispose();
        this.element.remove();
        this.observer.disconnect();
        this.element = null;
        this.observer = null;
        this.controls = null;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.viewport = null;
        this.buildings = null;
        this.composer = null;
        this.sphere = null;
        this.activeBuilding = null;
        updateComponentsOnCanvasResize();
    }

    set spherePosition(vec) {
        this.sphere.position.copy(vec);
    }

    getEquirectangular(size) {
        const cubeRenderTarget = new WebGLCubeRenderTarget(size);
        let cubeCamera;

        if (config.features.vantage.balcony.active) {
            cubeCamera = new CubeCamera2(0.1,8000,cubeRenderTarget,this.scene,this.sphereScene);
        } else {
            cubeCamera = new CubeCamera2(0.1,8000,cubeRenderTarget,this.sphereScene,this.scene);
        }

        cubeCamera.position.copy(this.camera.position);
        cubeCamera.update(this.renderer);

        const equirectangular = new CubemapToEquirectangular(this.renderer,size,false);
        equirectangular.convert(cubeCamera);
    }

}

class CubeCamera2 extends CubeCamera{
    constructor (near,far,renderTarget,scene1,scene2) {
        super(near,far,renderTarget);

        this.scene1 = scene1;
        this.scene2 = scene2;

        this.coordinateSystem = 2000;
        this.updateCoordinateSystem();
    }

    update( renderer ) {

        if ( this.parent === null ) this.updateMatrixWorld();

        const renderTarget = this.renderTarget;

        const [ cameraPX, cameraNX, cameraPY, cameraNY, cameraPZ, cameraNZ ] = this.children;

        const currentRenderTarget = renderer.getRenderTarget();

        const currentToneMapping = renderer.toneMapping;
        const currentXrEnabled = renderer.xr.enabled;
        const autoClear = renderer.autoClear;

        renderer.toneMapping = NoToneMapping;
        renderer.xr.enabled = false;
        renderer.autoClear = false;

        const generateMipmaps = renderTarget.texture.generateMipmaps;

        renderTarget.texture.generateMipmaps = false;

        renderer.setRenderTarget( renderTarget, 0 );
        renderer.render( this.scene1, cameraPX );
        renderer.render( this.scene2, cameraPX );

        renderer.setRenderTarget( renderTarget, 1 );
        renderer.render( this.scene1, cameraNX );
        renderer.render( this.scene2, cameraNX );

        renderer.setRenderTarget( renderTarget, 2 );
        renderer.render( this.scene1, cameraPY );
        renderer.render( this.scene2, cameraPY );

        renderer.setRenderTarget( renderTarget, 3 );
        renderer.render( this.scene1, cameraNY );
        renderer.render( this.scene2, cameraNY );

        renderer.setRenderTarget( renderTarget, 4 );
        renderer.render( this.scene1, cameraPZ );
        renderer.render( this.scene2, cameraPZ );

        renderTarget.texture.generateMipmaps = generateMipmaps;

        renderer.setRenderTarget( renderTarget, 5 );
        renderer.render( this.scene1, cameraNZ );
        renderer.render( this.scene2, cameraNZ );

        renderer.setRenderTarget( currentRenderTarget );

        renderer.toneMapping = currentToneMapping;
        renderer.xr.enabled = currentXrEnabled;
        renderer.autoClear = autoClear;

        renderTarget.texture.needsPMREMUpdate = true;

    }

}

function convertDataFromJSON (object) {
    const data = {};
    Object.entries(object).forEach(([key,value]) => {
        if (typeof value === 'string') return;
        if (typeof value === 'number') {
            data[key] = value;
        } else {
            if (key === 'rotation') {
                data[key] = new Euler().fromArray(value);
            } else {
                data[key] = new Vector3().fromArray(value);
            }
        }
    });

    return data;
}

window.vantage = Vantages;
